import { normalizeMemberPostalCode, resolveMemberCountryCode } from "./memberLocation";

export type GeoPoint = {
  latitude: number;
  longitude: number;
};

const UK_POSTCODE_PATTERN = /\b([A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2})\b/i;
const POSTCODE_LOOKUP_CACHE = new Map<string, GeoPoint | null>();
const EARTH_RADIUS_MILES = 3958.7613;

const toRadians = (value: number) => (value * Math.PI) / 180;

export const normalizeUkPostcode = (value: string | null | undefined) =>
  normalizeMemberPostalCode('GB', value) ?? '';

export const extractUkPostcode = (value: string | null | undefined) => {
  const matched = (value ?? '').toUpperCase().match(UK_POSTCODE_PATTERN)?.[1] ?? null;
  return matched ? normalizeUkPostcode(matched) : null;
};

export const isValidGeoPoint = (value: GeoPoint | null | undefined): value is GeoPoint =>
  Boolean(
    value &&
      Number.isFinite(value.latitude) &&
      Number.isFinite(value.longitude) &&
      Math.abs(value.latitude) <= 90 &&
      Math.abs(value.longitude) <= 180,
  );

export const calculateDistanceMiles = (left: GeoPoint, right: GeoPoint) => {
  const latDelta = toRadians(right.latitude - left.latitude);
  const lonDelta = toRadians(right.longitude - left.longitude);
  const leftLat = toRadians(left.latitude);
  const rightLat = toRadians(right.latitude);
  const haversine =
    Math.sin(latDelta / 2) ** 2 +
    Math.cos(leftLat) * Math.cos(rightLat) * Math.sin(lonDelta / 2) ** 2;

  return 2 * EARTH_RADIUS_MILES * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
};

export const lookupUkPostcodeCoordinates = async (postcode: string | null | undefined): Promise<GeoPoint | null> => {
  const normalized = normalizeUkPostcode(postcode);
  if (!normalized) {
    return null;
  }

  if (POSTCODE_LOOKUP_CACHE.has(normalized)) {
    return POSTCODE_LOOKUP_CACHE.get(normalized) ?? null;
  }

  try {
    const response = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(normalized)}`);
    if (!response.ok) {
      POSTCODE_LOOKUP_CACHE.set(normalized, null);
      return null;
    }

    const payload = (await response.json()) as {
      result?: { latitude?: number | null; longitude?: number | null } | null;
    };
    const latitude = payload.result?.latitude;
    const longitude = payload.result?.longitude;
    const coordinates =
      Number.isFinite(latitude) && Number.isFinite(longitude)
        ? ({ latitude: Number(latitude), longitude: Number(longitude) } satisfies GeoPoint)
        : null;

    POSTCODE_LOOKUP_CACHE.set(normalized, coordinates);
    return coordinates;
  } catch {
    POSTCODE_LOOKUP_CACHE.set(normalized, null);
    return null;
  }
};

export const lookupMemberCoordinates = async (
  countryCode: string | null | undefined,
  postcode: string | null | undefined,
): Promise<GeoPoint | null> => {
  if (resolveMemberCountryCode(countryCode, postcode) !== 'GB') {
    return null;
  }

  return lookupUkPostcodeCoordinates(postcode);
};

export const lookupLocationCoordinates = async (location: string | null | undefined) => {
  const postcode = extractUkPostcode(location);
  return lookupUkPostcodeCoordinates(postcode);
};
